import React, { type ReactElement, useCallback } from 'react';

import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

import { AccessTokenModalType } from '../../common/constants/access-token';
import type { SupportedThirdParty } from '../../common/constants/import-type';
import { useContentForThirdParty, useUrlForThirdParty } from '../../common/utils';
import { useAccessTokenModalState } from '../../controllers/ui-state';
import { useThirdPartyAccessToken } from '../../services/third-party-access-token';
import AccessTokenModal from './main';
import messages from './messages';

type Props = {
	isOpen: boolean;
	onClose: () => void;
	onClickCancelButton: () => void;
	onClickSubmitButton: (accessToken: string) => void;
	isLoading?: boolean;
	hasErrorOccurred?: boolean;
	thirdParty: SupportedThirdParty;
	showCurrentAccount?: boolean;
};

const AddNewAccessTokenModal = ({
	isOpen,
	onClose,
	onClickCancelButton,
	onClickSubmitButton,
	isLoading,
	hasErrorOccurred,
	thirdParty,
}: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { addAccessTokenModalDescription } = useContentForThirdParty(thirdParty);
	const { generateAPIToken } = useUrlForThirdParty(thirdParty);

	const onCancel = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'cancelAddNewAccessToken');
		onClickCancelButton();
	}, [createAnalyticsEvent, onClickCancelButton]);

	const onSubmit = useCallback(
		(accessToken: string) => {
			fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'submitAddNewAccessToken');
			onClickSubmitButton(accessToken);
		},
		[createAnalyticsEvent, onClickSubmitButton],
	);

	return (
		<AccessTokenModal
			isOpen={isOpen}
			onClose={onClose}
			title={formatMessage(messages.addAccessTokenModalTitle)}
			description={
				addAccessTokenModalDescription &&
				formatMessage(addAccessTokenModalDescription, {
					br: <br />,
					link: (children): ReactElement => (
						<a href={generateAPIToken} target="_blank">
							{children}
						</a>
					),
				})
			}
			cancelButton={{
				text: formatMessage(messages.cancelButton),
				onClick: onCancel,
			}}
			submitButton={{
				text: formatMessage(messages.submitButton),
				onClick: onSubmit,
			}}
			isLoading={isLoading}
			hasErrorOccurred={hasErrorOccurred}
			thirdParty={thirdParty}
		/>
	);
};

const EditAccessTokenModal = ({
	isOpen,
	onClose,
	onClickCancelButton,
	onClickSubmitButton,
	isLoading,
	hasErrorOccurred,
	thirdParty,
	showCurrentAccount,
}: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { editAccessTokenModalDescription } = useContentForThirdParty(thirdParty);

	const onCancel = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'cancelEditAccessToken');
		onClickCancelButton();
	}, [createAnalyticsEvent, onClickCancelButton]);

	const onSubmit = useCallback(
		(accessToken: string) => {
			fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'submitEditAccessToken');
			onClickSubmitButton(accessToken);
		},
		[createAnalyticsEvent, onClickSubmitButton],
	);

	return (
		<AccessTokenModal
			isOpen={isOpen}
			onClose={onClose}
			title={formatMessage(messages.editTokenModalTitle)}
			description={
				editAccessTokenModalDescription &&
				formatMessage(editAccessTokenModalDescription, {
					br: <br />,
				})
			}
			showCurrentAccount={showCurrentAccount}
			cancelButton={{
				text: formatMessage(messages.cancelButton),
				onClick: onCancel,
			}}
			submitButton={{
				text: formatMessage(messages.continueButton),
				onClick: onSubmit,
			}}
			isLoading={isLoading}
			hasErrorOccurred={hasErrorOccurred}
			thirdParty={thirdParty}
		/>
	);
};

const ExpiredAccessTokenModal = ({
	isOpen,
	onClose,
	onClickCancelButton,
	onClickSubmitButton,
	isLoading,
	hasErrorOccurred,
	thirdParty,
}: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { invalidAccessTokenModalDescription } = useContentForThirdParty(thirdParty);

	const onCancel = useCallback(() => {
		fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'cancelExpiredAccessToken');
		onClickCancelButton();
	}, [createAnalyticsEvent, onClickCancelButton]);

	const onContinue = useCallback(
		(accessToken: string) => {
			fireUIAnalytics(createAnalyticsEvent({}), 'button clicked', 'continueExpiredAccessToken');
			onClickSubmitButton(accessToken);
		},
		[createAnalyticsEvent, onClickSubmitButton],
	);

	return (
		<AccessTokenModal
			isOpen={isOpen}
			onClose={onClose}
			title={formatMessage(messages.invalidAccessTokenModalTitle)}
			description={
				invalidAccessTokenModalDescription &&
				formatMessage(invalidAccessTokenModalDescription, {
					br: <br />,
				})
			}
			cancelButton={{
				text: formatMessage(messages.cancelImportButton),
				onClick: onCancel,
			}}
			submitButton={{
				text: formatMessage(messages.continueButton),
				onClick: onContinue,
			}}
			isLoading={isLoading}
			hasErrorOccurred={hasErrorOccurred}
			thirdParty={thirdParty}
		/>
	);
};

const AccessTokenModalContent = ({
	thirdParty,
	onCloseAndReturnToUploadStep,
	showCurrentAccount,
}: {
	thirdParty: SupportedThirdParty;
	onCloseAndReturnToUploadStep: () => void;
	showCurrentAccount?: boolean;
}) => {
	const [
		{ isModalOpen, modalType, shouldCloseAndReturnToUploadStep, onFinishAccessTokenValidation },
		{ resetAccessTokenModalState, setAccessTokenModalState, setRequestAuthenticationToken },
	] = useAccessTokenModalState();

	const { postAccessToken, hasErrorOccurred, isLoading } = useThirdPartyAccessToken({
		thirdParty,
		requestAuthenticationToken: setRequestAuthenticationToken,
	});

	const onClose = useCallback(() => {
		setAccessTokenModalState({
			isModalOpen: false,
		});
		onFinishAccessTokenValidation && onFinishAccessTokenValidation(false);
	}, [setAccessTokenModalState, onFinishAccessTokenValidation]);

	const handleCancelClick = useCallback(() => {
		shouldCloseAndReturnToUploadStep && onCloseAndReturnToUploadStep();
		resetAccessTokenModalState();
		onFinishAccessTokenValidation && onFinishAccessTokenValidation(false);
	}, [
		shouldCloseAndReturnToUploadStep,
		onCloseAndReturnToUploadStep,
		resetAccessTokenModalState,
		onFinishAccessTokenValidation,
	]);

	const handleSubmitClick = useCallback(
		(accessToken: string) => {
			postAccessToken(accessToken, thirdParty).then((isValid: boolean) => {
				if (isValid) {
					onFinishAccessTokenValidation && onFinishAccessTokenValidation(isValid);
					setAccessTokenModalState({
						isModalOpen: false,
					});
				}
			});
		},
		[onFinishAccessTokenValidation, postAccessToken, setAccessTokenModalState, thirdParty],
	);

	if (modalType === AccessTokenModalType.ADD_NEW_TOKEN) {
		return (
			<AddNewAccessTokenModal
				isOpen={isModalOpen}
				onClickCancelButton={handleCancelClick}
				onClickSubmitButton={handleSubmitClick}
				onClose={onClose}
				isLoading={isLoading}
				hasErrorOccurred={hasErrorOccurred}
				thirdParty={thirdParty}
			/>
		);
	}

	if (modalType === AccessTokenModalType.EDIT_TOKEN) {
		return (
			<EditAccessTokenModal
				isOpen={isModalOpen}
				onClickCancelButton={handleCancelClick}
				onClickSubmitButton={handleSubmitClick}
				onClose={onClose}
				isLoading={isLoading}
				hasErrorOccurred={hasErrorOccurred}
				thirdParty={thirdParty}
				showCurrentAccount={showCurrentAccount}
			/>
		);
	}

	return (
		<ExpiredAccessTokenModal
			isOpen={isModalOpen}
			onClose={onClose}
			onClickCancelButton={handleCancelClick}
			onClickSubmitButton={handleSubmitClick}
			isLoading={isLoading}
			hasErrorOccurred={hasErrorOccurred}
			thirdParty={thirdParty}
		/>
	);
};

const RequestAccessTokenModal = ({
	onCloseAndReturnToUploadStep,
	showCurrentAccount,
}: {
	onCloseAndReturnToUploadStep: () => void;
	showCurrentAccount?: boolean;
}) => {
	const [{ thirdParty, isModalOpen }] = useAccessTokenModalState();

	if (!thirdParty || !isModalOpen) {
		return null;
	}

	return (
		<AccessTokenModalContent
			thirdParty={thirdParty}
			onCloseAndReturnToUploadStep={onCloseAndReturnToUploadStep}
			showCurrentAccount={showCurrentAccount}
		/>
	);
};

export default RequestAccessTokenModal;
